import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type PropsWithChildren,
} from 'react';
import { AppState, type AppStateStatus } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';

import type { AuthTokens, BudgetUser } from '../types/auth';
import { authService } from '../modules/Auth/services/authService';
import {
  ApiError,
  classifyRefreshFailure,
  configureApiAuth,
  isAuthFailureStatus,
  type TokenRefreshResult,
} from '../services/apiClient';
import { purgeLegacyFinanceCaches } from '../core/storage/purgeLegacyFinanceCaches';
import { telemetry } from '../services/telemetry';
import { markPendingLoginNotificationPrompt } from '../services/loginNotificationPromptGate';
import {
  ACCESS_TOKEN_REFRESH_BUFFER_MS,
  getJwtExpiryMs,
  shouldRefreshAccessToken,
} from '../utils/jwtExpiry';

/** Structured reasons for clearing (or keeping) session — used for logout debugging. */
export type AuthLogoutReason = 'user' | 'boot' | 'refresh_401';

type AuthContextValue = {
  user: BudgetUser | null;
  tokens: AuthTokens | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (tokens: AuthTokens, user: BudgetUser) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<BudgetUser | null>;
  refreshTokens: () => Promise<boolean>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const STORAGE_KEYS = {
  ACCESS_TOKEN: '@budget_access_token',
  REFRESH_TOKEN: '@budget_refresh_token',
  USER_DATA: '@budget_user_data',
};

export function AuthProvider({ children }: PropsWithChildren) {
  const [user, setUser] = useState<BudgetUser | null>(null);
  const [tokens, setTokens] = useState<AuthTokens | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const isAuthenticated = !!user && !!tokens;
  const tokensRef = useRef<AuthTokens | null>(null);
  tokensRef.current = tokens;
  const proactiveRefreshInFlight = useRef<Promise<TokenRefreshResult> | null>(null);

  const clearStoredAuth = useCallback(async () => {
    await Promise.all([
      AsyncStorage.removeItem(STORAGE_KEYS.ACCESS_TOKEN),
      AsyncStorage.removeItem(STORAGE_KEYS.REFRESH_TOKEN),
      AsyncStorage.removeItem(STORAGE_KEYS.USER_DATA),
    ]);
    tokensRef.current = null;
    setTokens(null);
    setUser(null);
  }, []);

  const clearSession = useCallback(
    async (reason: AuthLogoutReason) => {
      console.warn('[AuthContext] 🔒 Clearing session', { reason });
      await purgeLegacyFinanceCaches();
      await clearStoredAuth();
    },
    [clearStoredAuth],
  );

  const persistAuth = useCallback(async (nextTokens: AuthTokens, nextUser: BudgetUser) => {
    // Make the token readable to in-flight fetches before React re-renders.
    tokensRef.current = nextTokens;
    await Promise.all([
      AsyncStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, nextTokens.accessToken),
      AsyncStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, nextTokens.refreshToken),
      AsyncStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(nextUser)),
    ]);
    setTokens(nextTokens);
    setUser(nextUser);
    void purgeLegacyFinanceCaches();
  }, []);

  const refreshTokensInternal = useCallback(async (): Promise<TokenRefreshResult> => {
    try {
      const storedRefresh =
        (await AsyncStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN)) ??
        tokensRef.current?.refreshToken;

      if (!storedRefresh) return { ok: false, reason: 'no_token' };

      const data = await authService.refreshToken(storedRefresh);
      const nextTokens: AuthTokens = {
        accessToken: data.token,
        refreshToken: data.refreshToken,
      };

      tokensRef.current = nextTokens;
      await Promise.all([
        AsyncStorage.setItem(STORAGE_KEYS.ACCESS_TOKEN, nextTokens.accessToken),
        AsyncStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, nextTokens.refreshToken),
      ]);
      setTokens(nextTokens);
      return { ok: true };
    } catch (error) {
      const reason = classifyRefreshFailure(error);
      if (reason === 'network') {
        console.info('[AuthContext] network_kept_session', {
          context: 'token_refresh',
          detail: error instanceof Error ? error.message : 'refresh failed',
        });
      } else {
        console.error('[AuthContext] ❌ Auth refresh failed', error);
      }
      return { ok: false, reason };
    }
  }, []);

  const refreshTokens = useCallback(async (): Promise<boolean> => {
    const result = await refreshTokensInternal();
    return result.ok;
  }, [refreshTokensInternal]);

  const logout = useCallback(async () => {
    void telemetry.logLogout();
    await clearSession('user');
  }, [clearSession]);

  const loadStoredAuth = useCallback(async () => {
    try {
      const [accessToken, refreshToken, userData] = await Promise.all([
        AsyncStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN),
        AsyncStorage.getItem(STORAGE_KEYS.REFRESH_TOKEN),
        AsyncStorage.getItem(STORAGE_KEYS.USER_DATA),
      ]);

      if (accessToken && refreshToken && userData) {
        const storedTokens = { accessToken, refreshToken };
        tokensRef.current = storedTokens;
        setTokens(storedTokens);
        setUser(JSON.parse(userData) as BudgetUser);
        void purgeLegacyFinanceCaches();

        try {
          const response = await authService.getProfile(accessToken);
          if (response.user) {
            await AsyncStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
            setUser(response.user);
          }
        } catch (error) {
          // Only clear session on confirmed auth failures — never on offline / transient errors.
          const isUnauthorized =
            error instanceof ApiError &&
            isAuthFailureStatus(error.statusCode, error.message);

          if (isUnauthorized) {
            const refreshed = await refreshTokensInternal();
            if (refreshed.ok) {
              const nextAccess =
                (await AsyncStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) ??
                tokensRef.current?.accessToken;

              if (nextAccess) {
                try {
                  const response = await authService.getProfile(nextAccess);
                  if (response.user) {
                    await AsyncStorage.setItem(
                      STORAGE_KEYS.USER_DATA,
                      JSON.stringify(response.user),
                    );
                    setUser(response.user);
                  }
                  return;
                } catch (profileError) {
                  const profileUnauthorized =
                    profileError instanceof ApiError &&
                    isAuthFailureStatus(profileError.statusCode, profileError.message);

                  if (!profileUnauthorized) {
                    console.info('[AuthContext] network_kept_session', {
                      context: 'boot_profile_after_refresh',
                    });
                    return;
                  }

                  console.error(
                    '[AuthContext] ❌ Profile fetch failed after token refresh',
                    profileError,
                  );
                }
              }
            }

            // Keep cached user only when refresh failed for transient/offline reasons.
            // `no_token` / `auth` mean the session is actually invalid (or already cleared).
            if (!refreshed.ok && refreshed.reason === 'network') {
              console.info('[AuthContext] network_kept_session', {
                context: 'boot',
                refreshReason: refreshed.reason,
              });
              return;
            }

            await clearSession('boot');
            return;
          }

          console.info('[AuthContext] network_kept_session', {
            context: 'boot_profile',
            detail: error instanceof Error ? error.message : 'profile failed',
          });
        }
      }
    } catch (error) {
      console.error('[AuthContext] Error loading stored auth', error);
    } finally {
      setIsLoading(false);
    }
  }, [clearSession, refreshTokensInternal]);

  useEffect(() => {
    configureApiAuth({
      getAccessToken: async () => {
        return (
          tokensRef.current?.accessToken ??
          (await AsyncStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) ??
          null
        );
      },
      refreshTokens: refreshTokensInternal,
      onRefreshFailed: async (reason) => {
        await clearSession(reason);
      },
    });
    // Do not `configureApiAuth(null)` on cleanup — React Strict Mode remounts
    // this provider and in-flight dashboard fetches would go out unauthenticated.
    // The next effect pass overwrites handlers.
  }, [clearSession, refreshTokensInternal]);

  useEffect(() => {
    loadStoredAuth();
  }, [loadStoredAuth]);

  // Proactive access-token refresh ~1–2 min before expiry (access TTL is 15m).
  useEffect(() => {
    if (!tokens?.accessToken || !tokens.refreshToken) return;

    const expMs = getJwtExpiryMs(tokens.accessToken);
    if (expMs == null) return;

    const runProactiveRefresh = () => {
      if (proactiveRefreshInFlight.current) return;
      proactiveRefreshInFlight.current = refreshTokensInternal().finally(() => {
        proactiveRefreshInFlight.current = null;
      });
    };

    if (shouldRefreshAccessToken(tokens.accessToken)) {
      runProactiveRefresh();
      return;
    }

    const delay = Math.max(0, expMs - ACCESS_TOKEN_REFRESH_BUFFER_MS - Date.now());
    const timer = setTimeout(runProactiveRefresh, delay);
    return () => clearTimeout(timer);
  }, [tokens?.accessToken, tokens?.refreshToken, refreshTokensInternal]);

  // Refresh on foreground if the access token is near expiry.
  useEffect(() => {
    const onChange = (status: AppStateStatus) => {
      if (status !== 'active') return;
      const access = tokensRef.current?.accessToken;
      if (!shouldRefreshAccessToken(access)) return;
      if (proactiveRefreshInFlight.current) return;
      proactiveRefreshInFlight.current = refreshTokensInternal().finally(() => {
        proactiveRefreshInFlight.current = null;
      });
    };

    const sub = AppState.addEventListener('change', onChange);
    return () => sub.remove();
  }, [refreshTokensInternal]);

  const login = useCallback(
    async (nextTokens: AuthTokens, nextUser: BudgetUser) => {
      markPendingLoginNotificationPrompt();
      await persistAuth(nextTokens, nextUser);
      void telemetry.identify(nextUser);
      void telemetry.logLogin(nextUser.provider ?? 'unknown');
    },
    [persistAuth],
  );

  const refreshUser = useCallback(async (): Promise<BudgetUser | null> => {
    const accessToken =
      (await AsyncStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN)) ?? tokensRef.current?.accessToken;
    if (!accessToken) return null;

    const response = await authService.getProfile(accessToken);
    if (response.user) {
      await AsyncStorage.setItem(STORAGE_KEYS.USER_DATA, JSON.stringify(response.user));
      setUser(response.user);
      return response.user;
    }
    return null;
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      tokens,
      isLoading,
      isAuthenticated,
      login,
      logout,
      refreshUser,
      refreshTokens,
    }),
    [user, tokens, isLoading, isAuthenticated, login, logout, refreshUser, refreshTokens],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return ctx;
}
